import React, { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import './DashboardLayout.css';
import {
  CodeXml,
  Film,
  LayoutDashboard,
  Plus,
  Settings,
  Menu,
  X,
  MegaphoneIcon,
  User,
  Lock,
  LogOut,
  QrCode,
  Grid2X2
} from 'lucide-react';

import logo from "../../src/images/ctv_logo.png"
import { getUser } from '../api/auth';


export default function DashboardLayout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef();
  const navigate = useNavigate();

  const userDetail = getUser();

  const isActive = (path) => location.pathname === path;

  const closeSidebar = () => setSidebarOpen(false);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    // Clear auth here
    localStorage.clear();
    navigate("/sign-in");
  };

  return (
    <div className="dashboard-layout">

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="dashboard-overlay"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Mobile Close Button */}
        <button
          className="sidebar-close-btn"
          onClick={closeSidebar}
        >
          <X size={20} />
        </button>

        <Link
          to="/"
          className="dashboard-logo"
          onClick={closeSidebar}
        >
          <span className="dashboard-logo-icon">
            {/* <Tv className="h-8 w-8 text-primary" /> */}
            <img src={logo} alt='logo' className="h-12 w-12 text-black transform rotate-[180deg]" />
          </span>
          <span className="dashboard-logo-text">SocialTV</span>
        </Link>

        <Link
          to="/dashboard/editor"
          className={`dashboard-btn-new has-tooltip tooltip-right ${isActive('/dashboard/editor') ? 'active' : ''}`}
          data-tooltip="New Creative"
          onClick={closeSidebar}
        >
          <span className="dashboard-btn-plus">
            <Plus className="h-4 w-4" />
          </span>
          <span className="dashboard-btn-new-label">New Creative</span>
        </Link>

        <nav className="dashboard-nav">
          <Link
            to="/dashboard"
            className={`dashboard-nav-item has-tooltip tooltip-right ${isActive('/dashboard') ? 'active' : ''}`}
            data-tooltip="Dashboard"
            onClick={closeSidebar}
          >
            <span className="dashboard-nav-icon"><LayoutDashboard /></span>
            <span className="dashboard-nav-label">Dashboard</span>
          </Link>

          <Link
            to="/templates"
            className={`dashboard-nav-item has-tooltip tooltip-right ${isActive('/templates') ? 'active' : ''}`}
            data-tooltip="Templates"
            onClick={closeSidebar}
          >
            <span className="dashboard-nav-icon"><Grid2X2 /></span>
            <span className="dashboard-nav-label">Templates</span>
          </Link>

          <Link
            to="/creatives"
            className={`dashboard-nav-item has-tooltip tooltip-right ${isActive('/creatives') ? 'active' : ''}`}
            data-tooltip="Creatives"
            onClick={closeSidebar}
          >
            <span className="dashboard-nav-icon"><Film /></span>
            <span className="dashboard-nav-label">Creatives</span>
          </Link>

          <Link
            to="/campaign"
            className={`dashboard-nav-item has-tooltip tooltip-right ${isActive('/campaign') ? 'active' : ''}`}
            data-tooltip="Campaigns"
            onClick={closeSidebar}
          >
            <span className="dashboard-nav-icon"><MegaphoneIcon /></span>
            <span className="dashboard-nav-label">Campaigns</span>
          </Link>

          <Link
            to="/vast-tester"
            className={`dashboard-nav-item has-tooltip tooltip-right ${isActive('/xml-tester') ? 'active' : ''}`}
            data-tooltip="XML Tester"
            onClick={closeSidebar}
          >
            <span className="dashboard-nav-icon"><CodeXml /></span>
            <span className="dashboard-nav-label">XML Tester</span>
          </Link>
          <Link
            to="/qr-generator"
            className={`dashboard-nav-item has-tooltip tooltip-right ${isActive('/qr-generator') ? 'active' : ''}`}
            data-tooltip="Dynamic QR code"
            onClick={closeSidebar}
          >
            <span className="dashboard-nav-icon"><QrCode /></span>
            <span className="dashboard-nav-label">Dynamic QR code</span>
          </Link>
        </nav>

        {userDetail && userDetail?.user_name && (
          <div className="dashboard-nav-bottom">
            <div className="dashboard-sidebar-user" ref={dropdownRef}>
              <button
                type="button"
                className="dashboard-sidebar-user-trigger has-tooltip tooltip-right"
                data-tooltip="Account"
                onClick={() => setOpen(!open)}
              >
                <div className="dashboard-sidebar-user-avatar">
                  {userDetail?.user_name
                    ?.split(" ")
                    .map(word => word.charAt(0))
                    .join("")}
                </div>
              </button>

              {open && (
                <div className="dashboard-sidebar-dropdown">
                  <button
                    onClick={() => {
                      navigate('/profile');
                      setOpen(false);
                      closeSidebar();
                    }}
                    className="dashboard-sidebar-dropdown-btn"
                  >
                    <User size={18} />
                    Profile
                  </button>

                  <button
                    onClick={() => {
                      navigate('/change-password');
                      setOpen(false);
                      closeSidebar();
                    }}
                    className="dashboard-sidebar-dropdown-btn"
                  >
                    <Lock size={18} />
                    Change Password
                  </button>

                  <hr className="dashboard-sidebar-dropdown-hr" />

                  <button
                    onClick={() => {
                      handleLogout();
                      setOpen(false);
                      closeSidebar();
                    }}
                    className="dashboard-sidebar-dropdown-btn dashboard-sidebar-dropdown-btn-danger"
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                </div>
              )}
            </div>

            {/* <Link
            to="/settings"
            className="dashboard-nav-item has-tooltip"
            data-tooltip="Settings"
            onClick={closeSidebar}
          >
            <span className="dashboard-nav-avatar">
              <Settings />
            </span>
            <span className="dashboard-nav-label">Settings</span>
          </Link> */}
          </div>
        )}

      </aside>

      {/* Mobile Menu Button (no top header) */}
      <button
        className="menu-btn-fixed"
        onClick={() => setSidebarOpen(true)}
        type="button"
        aria-label="Open menu"
      >
        <Menu />
      </button>

      {/* Main */}
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="dashboard-body">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
